import { useEffect } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  FormHelperText,
  SelectChangeEvent,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchDepartements } from "../../reduxStore/DepartementSlice";
import { RootState, AppDispatch } from "../../reduxStore/Store";

/**
 * Choix des départements avec lesquels une entité (article, tiers…) est partagée
 * — migration 0130. Aucune sélection = partagée avec tous les départements.
 * Écran d'administration : liste complète des départements (pas l'endpoint /allowed).
 */
interface SelectDepartementsPartageProps {
  value: number[];
  onChange: (ids: number[]) => void;
  label?: string;
  /** Texte affiché quand rien n'est sélectionné — défaut « Tous les départements ». */
  emptyLabel?: string;
  /** Aide sous le champ — défaut « Aucune sélection = partagé avec tous les départements ». */
  helperText?: string;
  disabled?: boolean;
}

/** Parse le CSV `departements_partage_ids` renvoyé par les SP de sélection. */
export const parsePartageIds = (csv?: string | null): number[] =>
  csv ? csv.split(",").map(Number).filter((id) => id > 0) : [];

const SelectDepartementsPartage: React.FC<SelectDepartementsPartageProps> = ({
  value,
  onChange,
  label = "Départements",
  emptyLabel = "Tous les départements",
  helperText = "Aucune sélection = partagé avec tous les départements",
  disabled = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { departements, loading } = useSelector((state: RootState) => state.DepartementSlice);

  useEffect(() => {
    dispatch(fetchDepartements());
  }, [dispatch]);

  const handleChange = (event: SelectChangeEvent<number[]>) => {
    const v = event.target.value;
    onChange(typeof v === "string" ? v.split(",").map(Number) : v);
  };

  const renderValue = (selected: number[]) =>
    selected.length === 0
      ? emptyLabel
      : departements
          .filter((d) => d.id !== undefined && selected.includes(d.id))
          .map((d) => d.nom)
          .join(", ");

  return (
    <FormControl fullWidth size="small" variant="outlined">
      <InputLabel id="select-departements-partage-label" shrink>
        {label}
      </InputLabel>
      <Select<number[]>
        labelId="select-departements-partage-label"
        multiple
        displayEmpty
        notched
        value={value}
        onChange={handleChange}
        label={label}
        disabled={loading || disabled}
        renderValue={renderValue}
      >
        {departements.map((dept) => (
          <MenuItem key={dept.id} value={dept.id}>
            <Checkbox size="small" checked={dept.id !== undefined && value.includes(dept.id)} />
            <ListItemText primary={dept.nom} />
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default SelectDepartementsPartage;
